import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { openGoogleDriveFolderPicker, saveGoogleDriveFolders } from '@/lib/googleDrivePicker';

// Registered as this app's Google OAuth redirect_uri (must be a single
// fixed URL, mirroring MicrosoftConsentCallback's /integrations/ms-callback
// — Google's OAuth client redirect URI is not per-tenant-slug either).
// Google redirects the browser here after the user approves (or denies)
// access; the SPA still has the Supabase session in localStorage, so this
// leg stays authenticated even though it isn't under /:slug/.
export default function GoogleConsentCallback() {
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const [status, setStatus] = useState<'working' | 'success' | 'error'>('working');
  const [message, setMessage] = useState('Finishing Google Drive connection…');
  // Resolved via auth_tenant_id() + a tenants lookup rather than
  // useAuth().tenantSlug — see MicrosoftConsentCallback for why.
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (authLoading || ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    (async () => {
      // Resolve regardless of outcome so "Back to Integrations" works on
      // the error path too, not just the success-redirect path.
      const { data: tenantId } = await supabase.rpc('auth_tenant_id');
      if (tenantId) {
        const { data: tenant } = await supabase
          .from('tenants')
          .select('slug')
          .eq('id', tenantId)
          .maybeSingle();
        setTenantSlug(tenant?.slug ?? null);
      }

      const { data, error: invokeError } = await supabase.functions.invoke('google-consent-complete', {
        body: { code, state, error, error_description: errorDescription },
      });

      if (invokeError || !data?.success) {
        setStatus('error');
        setMessage(data?.error || invokeError?.message || 'Could not complete the Google Drive connection.');
        return;
      }

      // OAuth succeeded — now let the admin pick which folder(s) this
      // drive.file-scoped connection may see (see src/lib/googleDrivePicker.ts).
      // A picker failure/cancel doesn't undo the connection itself; it just
      // leaves it without folders selected yet, which the connector already
      // refuses to sync from — surfaced here as a distinct, non-error status
      // so "connected but no folders picked" reads differently from a real
      // failure.
      setMessage('Choose which Drive folder(s) to share…');
      try {
        const integrationId = (data as { integrationId?: string }).integrationId;
        const folders = integrationId ? await openGoogleDriveFolderPicker(integrationId) : null;
        if (integrationId && folders && folders.length > 0) {
          await saveGoogleDriveFolders(integrationId, folders);
          setStatus('success');
          setMessage(`Google Drive connected — syncing ${folders.length} folder${folders.length === 1 ? '' : 's'}.`);
        } else {
          setStatus('success');
          setMessage('Google Drive connected. Select folders to sync from the Integrations page whenever you\'re ready.');
        }
      } catch (pickerError) {
        setStatus('success');
        setMessage(
          `Google Drive connected, but folder selection failed (${pickerError instanceof Error ? pickerError.message : 'unknown error'}). ` +
          'Select folders from the Integrations page.',
        );
      }
    })();
  }, [authLoading]);

  useEffect(() => {
    if (status === 'success' && tenantSlug) {
      const t = setTimeout(() => navigate(`/${tenantSlug}/integrations`, { replace: true }), 1500);
      return () => clearTimeout(t);
    }
  }, [status, tenantSlug, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-sm text-center space-y-4">
        {status === 'working' && <Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" />}
        {status === 'success' && <CheckCircle2 className="h-10 w-10 mx-auto text-green-500" />}
        {status === 'error' && <XCircle className="h-10 w-10 mx-auto text-destructive" />}
        <p className="text-sm text-muted-foreground">{message}</p>
        {status === 'error' && tenantSlug && (
          <button
            className="text-sm underline text-primary"
            onClick={() => navigate(`/${tenantSlug}/integrations`, { replace: true })}
          >
            Back to Integrations
          </button>
        )}
      </div>
    </div>
  );
}
